import {
	FormControl,
	FormControlLabel,
	FormLabel,
	Checkbox,
	FormHelperText,
	FormGroup,
	Skeleton,
	Stack,
	Box,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormikProps } from 'formik';

import { useGetTestsQuery } from '../../../slices/apis/testApi';
import { fCurrency } from '../../../utils/formatNumber';

import { type TestProps, type Status, Test, Request } from '../../../models';

interface TestsComponentProps {
	formik: FormikProps<{
		supplierId: string;
		tests: TestProps[];
		status: Status;
	}>;
}

const Tests: React.FC<TestsComponentProps> = ({ formik }) => {
	const { data: tests } = useGetTestsQuery();
	const theme = useTheme();

	const ifTestChecked = Request.ifMarkTestChecked(formik.values.tests);

	const onTestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.checked
			? [
					...formik.values.tests,
					{
						id: e.target.name,
					},
				]
			: formik.values.tests.filter((test) => Number(test.id) !== Number(e.target.name));

		formik.setValues({
			...formik.values,
			tests: newValue.map(Test.normalise),
			// reset test centre when we change tests
			supplierId: '',
		});
	};

	const ifError = Boolean(formik.touched.tests) && Boolean(formik.errors.tests);

	const subtotal = tests
		? formik.values.tests.reduce((sum, selectedTest) => {
				const test = tests.find((test) => Number(test.id) === Number(selectedTest.id));
				return sum + (test ? test.price : 0);
			}, 0)
		: 0;

	return (
		<>
			{!tests ? (
				<Stack width={240} spacing={3}>
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
				</Stack>
			) : (
				<FormControl margin="normal" component="fieldset" variant="standard" error={ifError} sx={{ mt: 0 }}>
					<FormLabel component="legend">Tests</FormLabel>
					{ifError && <FormHelperText>{formik.errors.tests as string}</FormHelperText>}
					<FormGroup>
						{tests.map((test) => (
							<FormControlLabel
								key={test.id}
								control={
									<Checkbox
										name={test.id.toString()}
										checked={ifTestChecked(test)}
										onChange={onTestChange}
										disabled={Request.disableTestsEditing(formik.values.status)}
									/>
								}
								label={
									<Box
										sx={{
											display: 'flex',
											width: '100%',
											justifyContent: 'space-between',
											minWidth: {
												xs: 320,
												sm: 480,
											},
										}}
									>
										<span>{test.name}</span>
										<span>{fCurrency(test.price)}</span>
									</Box>
								}
							/>
						))}
					</FormGroup>

					{formik.values.tests.length > 0 && (
						<Box
							sx={{
								justifyContent: 'space-between',
								minWidth: 320,
								mt: 2,
								pr: 2,
							}}
						>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
								<Typography sx={{ fontWeight: 'bold' }}>Subtotal</Typography>
								<Typography sx={{ color: theme.palette.primary.main }}>${subtotal}</Typography>
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
								<Typography sx={{ fontWeight: 'bold' }}>GST (10%)</Typography>
								<Typography sx={{ color: theme.palette.primary.main }}>${(subtotal * 0.1).toFixed(2)}</Typography>
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
								<Typography sx={{ fontWeight: 'bold' }}>Total (inc. GST)</Typography>
								<Typography sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
									${(subtotal * 1.1).toFixed(2)}
								</Typography>
							</Box>
						</Box>
					)}
				</FormControl>
			)}
		</>
	);
};

export default Tests;
